import { Store } from '../patterns/package/store';

/**
 * Reactive store that reflects the reduce-motion preference.
 * True when either:
 * - OS-level `prefers-reduced-motion: reduce` is active, or
 * - The a11y widget's reduce-motion toggle is enabled
 *   (reflected as `data-a11y-reduce-motion` on body)
 *
 * Components subscribe once — no per-instance observers needed.
 */
export const reduceMotionStore = new Store<boolean>(window.matchMedia('(prefers-reduced-motion: reduce)').matches);

/**
 * Initializes reduce-motion tracking from both OS media query and the
 * a11y widget body attribute. Keeps `reduceMotionStore` and the body
 * attribute in sync.
 *
 * Call once at app boot.
 */
export function initReduceMotion(): void {
  const mq = window.matchMedia('(prefers-reduced-motion: reduce)');

  const apply = () => {
    // checks for the presence of data-a11y-reduce-motion on <body>
    const active = mq.matches || 'a11yReduceMotion' in document.body.dataset;
    document.body.toggleAttribute('data-a11y-reduce-motion', active);
    reduceMotionStore.next(active);
  };

  // Apply initial state
  apply();

  // React to OS-level changes
  mq.addEventListener('change', apply);

  // React to a11y widget toggling the body attribute
  const observer = new MutationObserver(apply);
  observer.observe(document.body, { attributes: true, attributeFilter: ['data-a11y-reduce-motion'] });
}
